import { Box, Checkbox, createStyles, Group, Stack } from '@mantine/core';
import type { MonitorActionNotification } from '@repo/api-codegen';
import { SettingsBlock, SettingsButton } from '@repo/common/components';
import { observer } from 'mobx-react-lite';

interface MonitorNotificationFormProps {
	value?: MonitorActionNotification;
	onChange: (value: MonitorActionNotification) => void;
}

const NOTIFICATION_CONFIG = [
	{
		key: 'owners',
		label: 'Owners of monitor',
		defaultValue: true,
	},
	{
		key: 'subscribers',
		label: 'Subscribers of monitor',
		defaultValue: true,
	},
	{
		key: 'resource_owners',
		label: 'Owners of resource',
		defaultValue: true,
	},
	{
		key: 'resource_subscribers',
		label: 'Subscribers of resource',
		defaultValue: true,
	},
	{
		key: 'downstream_owners',
		label: 'Owners of downstream resources',
		defaultValue: false,
	},
	{
		key: 'downstream_subscribers',
		label: 'Subscribers of downstream resources',
		defaultValue: false,
	},
] as const;

const DEFAULT_NOTIFICATION_VALUES = NOTIFICATION_CONFIG.reduce(
	(acc, config) => ({
		...acc,
		[config.key]: config.defaultValue,
	}),
	{} as MonitorActionNotification
);

const useStyles = createStyles((theme) => ({
	checkboxItem: {
		backgroundColor: 'transparent',
		borderRadius: theme.radius.sm,
	},
}));

export const MonitorNotificationForm = observer(
	({
		value = DEFAULT_NOTIFICATION_VALUES,
		onChange,
	}: MonitorNotificationFormProps) => {
		const { classes } = useStyles();

		// Ensure we always have a valid notification object
		const notificationValue = value || DEFAULT_NOTIFICATION_VALUES;

		const handleToggle = (key: keyof MonitorActionNotification) => {
			onChange({
				...notificationValue,
				[key]: !notificationValue[key],
			});
		};

		return (
			<Box>
				<Box mb="4xl">
					<SettingsButton
						variant="tertiary"
						title="Users"
						tooltip="Notification settings"
						aria-label="Notification settings"
						description="Notify by in-app notifications, Slack, and email"
					/>

					<SettingsBlock
						title=""
						description={
							<Stack spacing="sm">
								{NOTIFICATION_CONFIG.map((config) => (
									<Group
										key={config.key}
										position="apart"
										className={classes.checkboxItem}
									>
										<Checkbox
											label={config.label}
											checked={notificationValue[config.key]}
											onChange={() => handleToggle(config.key)}
										/>
									</Group>
								))}
							</Stack>
						}
					/>
				</Box>
			</Box>
		);
	}
);
